/**
 * Ticker Explorer's cache rule. There is one.
 *
 * **Open a ticker, and everything is fetched once.** Every click after that —
 * any tab, back to Overview, back and forth all day — costs nothing. Ten
 * minutes after a fetch the data is marked stale, and the next thing the
 * reader *does* refreshes it: a tab switch, or a reload. Nothing refreshes on
 * its own, ever.
 *
 * ⚠️ **`gcTime: Infinity` is load-bearing, not laziness.** It is React Query's
 * garbage collector, and its clock runs only while nothing on screen is using
 * a query. This page mounts ONE section at a time — clicking Gamma does not
 * hide Overview, it unmounts it — so every query here spends most of its life
 * unobserved. With a finite `gcTime` the data is deleted out from under a
 * perfectly valid `staleTime`, and coming back re-fetches in full however
 * fresh the data nominally was. Two timers that disagree means the shorter one
 * silently wins. So there is only one timer, and it is `staleTime`.
 *
 * ⚠️ **The no-polling part is a UX decision, not only a cost one.** It was
 * asked for directly: a page that rewrites the numbers under someone reading
 * them is worse than a slightly old one. If provider calls become cheap the
 * *window* can shorten — but do not restore background polling without
 * asking. See §9e of the handoff for what else is safe to undo and what is
 * not.
 *
 * ⚠️ **This deliberately does NOT vary by data type.** An earlier version had
 * separate windows for session data, daily data and open interest, keyed to
 * the 06:45 ET publication boundary. That was solving a problem in the wrong
 * place: the reason a refetch was expensive is that the client walked the
 * option chain itself, up to nine pages for SPY. `server/calendar_sync` caches
 * that per ticker per OI day, so a client refetch is ONE request to the portal
 * regardless of what is behind it. Once the cost of refetching is flat, the
 * client has no reason to care what kind of data it is holding.
 *
 * Memory is not a concern: contract snapshots are trimmed to ~100 rows before
 * caching (see `trim` in optionContractsService), which is tens of KB per
 * ticker.
 */
/** The one window. Ten minutes. */
export const WINDOW_MS = 10 * 60 * 1000;

export const EXPLORER_CACHE = {
  /**
   * How long a fetch is trusted.
   *
   * Checked only when a component mounts — so it is the answer to "I came back
   * to this tab, is it worth refetching?", never a background timer.
   */
  staleTime: WINDOW_MS,

  /** Never dropped while the page is open. See the note above. */
  gcTime: Infinity,

  // A page that rewrites the numbers being read is worse than a slightly old
  // one. Both of these would do exactly that.
  refetchOnWindowFocus: false,
  refetchOnReconnect: false,
} as const;

/**
 * The window that remains for data that was already some age when it arrived.
 *
 * ⚠️ **Without this, the client and server windows STACK.** The contract
 * snapshot is a shared server-side row that is topped up at most every ten
 * minutes, so it can already be nine minutes old when it is handed over. A
 * client window measured from its own fetch then holds it ten minutes more,
 * and the reader is looking at nineteen-minute-old volume while the system
 * claims ten.
 *
 * It also splits viewers into staggered lanes: whoever arrives first triggers
 * the top-up and sees fresh numbers, and everyone who arrives behind them
 * stays a fixed distance behind all session, purely by accident of arrival
 * time.
 *
 * Measuring from the data's own timestamp collapses both problems — the
 * reader's view is at most ten minutes old whoever they are, and it costs no
 * extra provider calls, because the server's own TTL still gates whether a
 * request actually refreshes anything.
 *
 * Clock skew is clamped in both directions: a client running fast would
 * otherwise compute a huge age and refetch on every mount, and one running
 * slow would compute a negative age and hold forever.
 */
export function remainingWindow(asOf: string | null | undefined): number {
  if (!asOf) return WINDOW_MS;

  const stamped = new Date(asOf).getTime();
  if (!Number.isFinite(stamped)) return WINDOW_MS;

  const age = Math.min(Math.max(Date.now() - stamped, 0), WINDOW_MS);
  return WINDOW_MS - age;
}
